import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { CONFIGURATION_FEATURES_NAMES } from '~/Types';

import { isFeatureEnabled, reportAxiosError, subOrgIdToNameDict } from '../../../../../Utils';
import { useSysconfig } from '../../../SystemConfigurationScreen';
import { validateConditionsTree } from '../../Conditions/QueryBuilder';
import {
  convertFormikValuesToServerValues,
  getValidationSchemaShape,
  ruleToInitialValue,
  useNotificationConfigurationTool,
} from '../utils';

import { NotificationDialogInner } from './NotificationDialogInner';

export const NotificationDialog = ({ rule, onSubmit, onClose, existingRuleNames, conditions, events }) => {
  const { organization } = useSysconfig();
  const subOrgIdToName = subOrgIdToNameDict(organization);
  const subOrgsRequired = organization.sub_organizations_enabled;
  const isQueryBuilderFeatureEnabled = isFeatureEnabled(
    organization,
    CONFIGURATION_FEATURES_NAMES.NOTIFICATION_RULES_QUERY_BUILDER
  );
  const { recipientTypes } = useNotificationConfigurationTool();
  // The QueryBuilder wrapper hands us the fetched FieldDefinition list via
  // onFieldsLoaded; we hold it here so the form-level `validate` below can walk
  // values.conditions_json against the same registry the builder offered.
  const [conditionFields, setConditionFields] = useState([]);

  return (
    <Formik
      initialValues={ruleToInitialValue(rule)}
      validationSchema={Yup.object().shape(
        getValidationSchemaShape(
          existingRuleNames,
          conditions,
          subOrgsRequired,
          recipientTypes,
          isQueryBuilderFeatureEnabled
        )
      )}
      enableReinitialize
      validate={(values) => {
        // The builder is only offered on event-based rules, so a tree it wrote always has an
        // event to measure "When the event occurred" against.
        const treeError = validateConditionsTree(values.conditions_json, conditionFields, {
          hasTriggeringEvent: true,
        });
        return treeError ? { conditions_json: treeError } : {};
      }}
      onSubmit={async (values, { setSubmitting }) => {
        try {
          await onSubmit(
            convertFormikValuesToServerValues(values, organization, isQueryBuilderFeatureEnabled),
            rule?.id
          );
          onClose();
        } catch (error) {
          await reportAxiosError(error);
          setSubmitting(false);
        }
      }}
    >
      <NotificationDialogInner
        rule={rule}
        onClose={onClose}
        subOrgIdToName={subOrgIdToName}
        events={events}
        conditions={conditions}
        onConditionFieldsLoaded={setConditionFields}
      />
    </Formik>
  );
};

NotificationDialog.propTypes = {
  rule: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  existingRuleNames: PropTypes.arrayOf(PropTypes.string).isRequired,
  conditions: PropTypes.object.isRequired,
  events: PropTypes.array.isRequired,
};
